/**
 * `ui.route_change` on every navigation: `history.pushState` / `replaceState` and
 * `popstate`.
 *
 * A second install on the same window is a no-op (a sentinel on the window).
 * One event per change of `pathname + search` — a write that leaves them as they were
 * (a hash-only change, a replace with the same URL) records nothing. The event keeps
 * the current `url` (path + search, never the fragment) and adds `from` / `to` route
 * templates (lib/view/routeTemplate.ts) and `via`: `push`, `replace` or `pop`.
 */

import { recordUiEvent } from "../lib/logRingBuffer";
import { pathAndSearch } from "../lib/telemetry/sanitize.js";
import { routeTemplateOf } from "../lib/view/routeTemplate.js";

const INSTALLED = Symbol.for("sooth.telemetry.routeCapture");

/**
 * Wrap the window's history writes and listen for `popstate`. Returns false when
 * there is no history to wrap or this window already has the capture.
 */
export function installRouteCapture({
  win = typeof window !== "undefined" ? window : null,
  record = recordUiEvent,
} = {}) {
  if (!win || !win.history || !win.location || win[INSTALLED]) return false;
  win[INSTALLED] = true;
  let last = pathAndSearch(win.location);

  function note(via) {
    try {
      const next = pathAndSearch(win.location);
      if (next === last) return;
      const from = last;
      last = next;
      record({
        event: "ui.route_change",
        via,
        url: next,
        from: routeTemplateOf(from),
        to: routeTemplateOf(next),
      });
    } catch {
      /* telemetry must never break navigation */
    }
  }

  for (const [method, via] of [
    ["pushState", "push"],
    ["replaceState", "replace"],
  ]) {
    const original = win.history[method];
    if (typeof original !== "function") continue;
    win.history[method] = function (...args) {
      const out = original.apply(this, args);
      note(via);
      return out;
    };
  }
  win.addEventListener("popstate", () => note("pop"));
  return true;
}
